import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ChevronLeft, ChevronRight, UserRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

// Page « Les intervenants » : la liste des personnes qui font RAV, et chaque
// fiche s'OUVRE au toucher (photo, nom, profession, présentation) — demande Julie.
// Les fiches se gèrent dans l'admin Julie (onglet Intervenants).
export default function Intervenants() {
  const [people, setPeople] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/intervenants`)
      .then(({ data }) => setPeople(data.intervenants || []))
      .catch(() => {});
  }, []);

  const selected = people.find((p) => p.id === selectedId) || null;

  const photo = (p, cls, testId) =>
    p.photo ? (
      <img src={`${API}/media/${p.photo}`} alt={p.name} className={cls} data-testid={testId} loading="lazy" decoding="async" />
    ) : (
      <span className={`${cls} rav-person-photo-empty`} data-testid={testId} aria-hidden="true">
        <UserRound size={cls.includes("detail") ? 52 : 34} strokeWidth={1.4} />
      </span>
    );

  return (
    <main className="rav-page" data-testid="intervenants-page">
      <motion.img
        src="/logo-accueil.webp"
        alt="Respire Aime Vibre"
        className="rav-page-logo"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
      />
      <AnimatePresence mode="wait">
        {selected ? (
          /* ---------- Fiche ouverte ---------- */
          <motion.div
            key="detail"
            data-testid="intervenant-detail"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <button type="button" className="rav-text-link rav-person-back" data-testid="intervenant-back" onClick={() => setSelectedId(null)}>
              <ChevronLeft size={15} strokeWidth={1.8} /> Toutes les intervenantes
            </button>
            <div className="rav-person-detail">
              {photo(selected, "rav-person-photo rav-person-detail-photo", "intervenant-detail-photo")}
              <h1 className="rav-person-name rav-person-detail-name" data-testid="intervenant-detail-name">{selected.name}</h1>
              {selected.title && <p className="rav-person-role" data-testid="intervenant-detail-role">{selected.title}</p>}
              {selected.presentation && <p className="rav-person-bio rav-person-detail-bio" data-testid="intervenant-detail-bio">{selected.presentation}</p>}
              {selected.skills && (
                <p className="rav-person-meta" data-testid="intervenant-detail-skills">
                  <span>Ses compétences</span>
                  {selected.skills}
                </p>
              )}
              {selected.availability && (
                <p className="rav-person-meta" data-testid="intervenant-detail-availability">
                  <span>Ses disponibilités</span>
                  {selected.availability}
                </p>
              )}
            </div>
          </motion.div>
        ) : (
          /* ---------- Liste ---------- */
          <motion.div key="list" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.5, ease: EASE }}>
            <h1 className="rav-page-title" data-testid="intervenants-title">Les intervenants</h1>
            <p className="rav-page-sub">Les visages qui font Respire Aime Vibre — touchez une personne pour la découvrir.</p>

            {people.length === 0 ? (
              <p className="rav-page-sub rav-explorer-hint" data-testid="intervenants-empty">
                Les présentations arrivent bientôt.
              </p>
            ) : (
              <div className="rav-people">
                {people.map((p, i) => (
                  <motion.button
                    key={p.id}
                    type="button"
                    className="rav-person-card rav-person-btn"
                    data-testid={`intervenant-${i}`}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.15 + i * 0.08, ease: EASE }}
                    onClick={() => setSelectedId(p.id)}
                    aria-label={`Découvrir la fiche de ${p.name}`}
                  >
                    {photo(p, "rav-person-photo", `intervenant-photo-${i}`)}
                    <span className="rav-person-body">
                      <span className="rav-person-name" data-testid={`intervenant-name-${i}`}>{p.name}</span>
                      {p.title && <span className="rav-person-role" data-testid={`intervenant-role-${i}`}>{p.title}</span>}
                    </span>
                    <ChevronRight size={18} strokeWidth={1.6} className="rav-person-chevron" aria-hidden="true" />
                  </motion.button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
